import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { ALPHA_LETTERS } from '../../lib/clientGrouping';

// iOS Contacts-style touch-scrubbable letter index. Tap a letter to jump,
// or drag a finger along the strip to scrub through sections.
//
// Why this is implemented with native event listeners + direct scrollTop
// manipulation, NOT React's onTouchMove + scrollIntoView: prior versions
// of this component bled scroll into ancestors. Two root causes:
//
//   1. React 18's synthetic touch events are PASSIVE by default — calling
//      e.preventDefault() inside onTouchMove is a silent no-op, so the
//      browser keeps doing native touch-scroll alongside our handler.
//      Fix: register touchstart/touchmove via addEventListener with
//      `{ passive: false }`, where preventDefault actually works.
//
//   2. scrollIntoView walks UP the DOM looking for any scrollable ancestor
//      that could bring the target into view, then scrolls them all. Even
//      with body-scroll-lock, modal containers or other parents could
//      respond. Fix: cache each section heading's offsetTop relative to
//      the scroll container, and set scrollContainer.scrollTop directly.
//      That mutation is bounded to the one element — no ancestor can
//      possibly respond.

export function AlphaJumpBar({
  presentLetters,
  ariaLabel,
  jumpToLabel,
  scrollContainerRef,
}: {
  presentLetters: Set<string>;
  ariaLabel: string;
  jumpToLabel: (L: string) => string;
  /** The list's scroll container. Required: this is what we mutate
   *  directly (scrollTop) to position the list, instead of asking a
   *  child element to scroll itself into view. */
  scrollContainerRef: RefObject<HTMLElement | null>;
}) {
  const allLetters = useMemo(() => [...ALPHA_LETTERS, '#'], []);
  const navRef = useRef<HTMLDivElement>(null);
  const [activeLetter, setActiveLetter] = useState<string | null>(null);

  /** Resolve the letter under a given clientY. */
  function letterFromClientY(clientY: number): string | null {
    const el = navRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const y = clientY - rect.top;
    const pct = Math.min(0.9999, Math.max(0, y / rect.height));
    const idx = Math.floor(pct * allLetters.length);
    return allLetters[idx] ?? null;
  }

  /** Jump the scroll container to the section whose heading is keyed by
   *  data-letter={letter}. Uses direct scrollTop math — does NOT call
   *  scrollIntoView (that's the bug we're fixing). */
  function jumpTo(letter: string) {
    const root = scrollContainerRef.current;
    if (!root) return;
    const heading = root.querySelector<HTMLElement>(`[data-letter="${letter}"]`);
    if (!heading) return;
    // offsetTop is relative to the nearest positioned ancestor. Compute
    // distance from the heading to the scroll container by walking up.
    let offset = 0;
    let el: HTMLElement | null = heading;
    while (el && el !== root) {
      offset += el.offsetTop;
      el = el.offsetParent as HTMLElement | null;
    }
    // Subtract a little so the section header doesn't sit flush against
    // the top edge — gives a few px of breathing room.
    root.scrollTop = Math.max(0, offset - 4);
  }

  // Native touch listener registration. Doing this via addEventListener
  // (not React's onTouchMove prop) is the only way to call preventDefault
  // successfully — React 18 attaches touch listeners as passive.
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;

    const onTouch = (e: TouchEvent) => {
      // preventDefault here actually works because the listener is
      // registered with `{ passive: false }`. Blocks the native touch-
      // scroll gesture so the browser doesn't try to drag the page in
      // parallel with our scrub.
      e.preventDefault();
      const touch = e.touches[0];
      if (!touch) return;
      const letter = letterFromClientY(touch.clientY);
      if (!letter) return;
      setActiveLetter(letter);
      if (presentLetters.has(letter)) jumpTo(letter);
    };

    const onTouchEnd = () => {
      // Brief delay so a quick scrub still registers the callout visually.
      window.setTimeout(() => setActiveLetter(null), 160);
    };

    el.addEventListener('touchstart', onTouch, { passive: false });
    el.addEventListener('touchmove',  onTouch, { passive: false });
    el.addEventListener('touchend',   onTouchEnd);
    el.addEventListener('touchcancel',onTouchEnd);
    return () => {
      el.removeEventListener('touchstart', onTouch);
      el.removeEventListener('touchmove',  onTouch);
      el.removeEventListener('touchend',   onTouchEnd);
      el.removeEventListener('touchcancel',onTouchEnd);
    };
    // presentLetters changes when the list filters; re-bind so the
    // closure captures the latest set. scrollContainerRef is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentLetters]);

  // Wheel: forward delta to the list, prevent the page from scrolling.
  // React's wheel listener IS non-passive by default for events with
  // ctrl-key / etc., but Chrome/Firefox treat onWheel as passive too in
  // some cases. Belt-and-suspenders: also register natively here.
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const target = scrollContainerRef.current;
      if (!target) return;
      e.preventDefault();
      target.scrollTop += e.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [scrollContainerRef]);

  return (
    <>
      <nav
        ref={navRef}
        aria-label={ariaLabel}
        // flex-col + each button flex-1 min-h-0 distributes the 27 letters
        // across whatever vertical space the bar's parent gives it. With
        // a max-h-4 cap on each button, the bar never grows uncomfortably
        // tall on big screens, AND it never spills off the bottom on small
        // ones — letters compress proportionally instead. The scrub works
        // either way since we compute letter-from-y from the rect bounds.
        //
        // z-20: must sit above the sticky section banners inside the scroll
        // container. Those banners are z-[1] (so they cover list rows when
        // pinned); the bar lives in the same parent stacking context as
        // the scroll container, so it needs an explicit z higher than the
        // banner's z-[1] or the banner overlaps the letter strip.
        className="absolute right-0 top-0 bottom-0 z-20 flex flex-col items-center px-1.5 py-1 select-none touch-none"
      >
        {allLetters.map((L) => {
          const has = presentLetters.has(L);
          const isActive = activeLetter === L;
          return (
            <button
              key={L}
              type="button"
              disabled={!has}
              onClick={() => has && jumpTo(L)}
              aria-label={jumpToLabel(L)}
              className={`text-[10px] font-extrabold leading-none w-4 flex-1 min-h-0 max-h-4 grid place-items-center transition-colors ${
                isActive
                  ? 'text-navy-900 dark:text-gold-500 scale-125'
                  : has
                  ? 'text-ink-700 hover:text-gold-500'
                  : 'text-ink-500/40'
              }`}
            >
              {L}
            </button>
          );
        })}
      </nav>

      {activeLetter && (
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 grid place-items-center z-40"
        >
          <div className="rounded-2xl bg-navy-900/90 text-gold-400 dark:bg-gold-500/95 dark:text-navy-900 font-display text-[64px] leading-none w-24 h-24 grid place-items-center shadow-2xl backdrop-blur-sm">
            {activeLetter}
          </div>
        </div>
      )}
    </>
  );
}
